import styled, { css } from 'styled-components';

import { media } from '@savgroup-front-common/constants';
import { spacing } from '@savgroup-front-common/primitive/helpers/spacing';
import {
  SUPPORTED_COMPONENTS,
  SUPPORTED_PROPERTIES,
} from '@savgroup-front-common/types';

import { styledGetCustomComponentProperty } from '../../../helpers';
import { Button } from '../../button';
import { InputStatus } from '../common/helpers/getFinalFieldState.types';
import { getWrapperHoverBackgroundFromStatus } from '../common/Wrapper/Wrapper.helpers';
import { getBorderColor } from '../Input/Input.helpers';

interface QuantityContainerProps {
  $status: InputStatus;
  $isFocused?: boolean;
  $isHovered?: boolean;
  fullWidth?: boolean;
  $isSmall?: boolean;
}

export const $QuantityContainer = styled.div<QuantityContainerProps>`
  display: flex;
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'inherit')};
  height: ${({ $isSmall }) => {
    if ($isSmall) {
      return 'fit-content';
    }

    return styledGetCustomComponentProperty({
      componentName: SUPPORTED_COMPONENTS.DEFAULT_INPUTS,
      property: SUPPORTED_PROPERTIES.HEIGHT,
    });
  }};

  transition-property: border;
  transition-duration: ${({ theme }) => theme.interactions.basicTiming};

  & input:hover,
  & input:focus {
    border-radius: 0;
    border-width: ${spacing('px')};
    border-style: solid;
    border-color: ${({ theme, $status }) => {
      return getWrapperHoverBackgroundFromStatus({ theme, $status });
    }};
  }

  @media ${media.maxWidth.sm} {
    width: 100%;
  }
`;

const buttonStyle = css`
  border: none;
  min-height: 0;
  &:hover,
  &:focus {
    border: none;
  }
`;

interface ButtonQuantityProps {
  $isSmall?: boolean;
}

const MOBILE_TOUCH_TARGET = spacing(11);

const quantityButtonSize = css<ButtonQuantityProps>`
  min-width: ${({ $isSmall }) => {
    if ($isSmall) {
      return spacing('xl');
    }

    return styledGetCustomComponentProperty({
      componentName: SUPPORTED_COMPONENTS.DEFAULT_INPUTS,
      property: SUPPORTED_PROPERTIES.HEIGHT,
    });
  }};
  height: ${({ $isSmall }) => ($isSmall ? spacing('xl') : 'unset')};

  ${({ $isSmall }) =>
    $isSmall &&
    css`
      & svg {
        width: ${spacing('md')};
        height: ${spacing('md')};
      }

      @media ${media.maxWidth.sm} {
        & svg {
          width: ${spacing('lg')};
          height: ${spacing('lg')};
        }
      }
    `}

  @media ${media.maxWidth.sm} {
    min-width: ${MOBILE_TOUCH_TARGET};
    height: ${MOBILE_TOUCH_TARGET};
    max-height: ${MOBILE_TOUCH_TARGET};
  }
`;

export const $PlusButtonQuantity = styled(Button)<ButtonQuantityProps>`
  ${buttonStyle};
  ${quantityButtonSize};
  border-radius: 0 ${({ theme }) => theme.tw.radius.sm}
    ${({ theme }) => theme.tw.radius.sm} 0;
`;

export const $MinusButtonQuantity = styled(Button)<ButtonQuantityProps>`
  ${buttonStyle};
  ${quantityButtonSize};
  border-radius: ${({ theme }) => theme.tw.radius.sm} 0 0
    ${({ theme }) => theme.tw.radius.sm};
`;

interface QuantityDisplayProps {
  $currentValue: string;
  $isFocused: boolean;
  $maxValue?: number;
  $status: InputStatus;
  $disabled?: boolean;
  $fullWidth?: boolean;
  $isSmall?: boolean;
}

export const $QuantityDisplay = styled.div<QuantityDisplayProps>`
  background-color: ${({ theme }) => theme.systemColors.defaultBackground};
  transition-duration: ${({ theme }) => theme.interactions.transformTiming};
  position: relative;

  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : null)};
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'fit-content')};
  min-width: 50px;

  flex: ${({ $fullWidth }) => ($fullWidth ? '1 100%' : null)};
  &:before {
    content: '${({ $currentValue, $maxValue }) => {
      if ($maxValue) {
        return `${$currentValue} / ${$maxValue}`;
      }

      return `${$currentValue}`;
    }}';
    position: absolute;
    text-align: center;
    top: 0px;
    left: 0px;
    display: ${({ $isFocused }) => ($isFocused ? 'none' : 'flex')};
    align-items: center;
    justify-content: center;
    width: 100%;
    height: ${({ $isSmall }) => ($isSmall ? '24px' : '100%')};
    color: ${({ theme }) => theme.colors.mainTextColor};
    background-color: ${({ theme }) => theme.systemColors.defaultBackground};
    border: 1px solid
      ${({ theme, $status, $disabled }) =>
        getBorderColor({ theme, status: $status, disabled: $disabled })};
  }

  && input {
    border-radius: 0px !important;
    opacity: ${({ $isFocused }) => ($isFocused ? '1' : '0')};
    padding: 0;
  }

  @media ${media.maxWidth.sm} {
    flex: 1;
    min-width: 0;

    &:before {
      height: 100%;
      font-size: 1rem;
    }

    && input {
      min-height: ${spacing(11)};
      font-size: 1rem;
    }
  }
`;
